import {
  Box,
  FormControlLabel,
  Switch,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import React, { useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { EditorState, ContentState } from "draft-js";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import htmlToDraft from "html-to-draftjs";
import { NameForm } from "../../../../AdminEventView/EventBasicInfo/NameForm";
import { DescriptionForm } from "../../../../AdminEventView/EventBasicInfo/DescriptionForm";
import { FormPrompt } from "../../../../FormPrompt/FormPrompt";
import { BottomButtons } from "../../../../AdminEventView/EventBasicInfo/BottomButtons";
import {
  createShow,
  updateShow,
} from "../../../../../redux/features/showsAndRoomsSlice";
import { useDispatch } from "react-redux";

const editorStateFromHtml = (html) => {
  const contentBlock = htmlToDraft(html);
  if (contentBlock) {
    const contentState = ContentState.createFromBlockArray(
      contentBlock.contentBlocks,
    );
    const editorState = EditorState.createWithContent(contentState);
    return editorState;
  }
};

const ShowBasicInfo = () => {
  const { organization, show, creatingNew } = useOutletContext();
  const { id: organizationId } = organization;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { id: showId, name, description, url, publicFacing } = show || {};

  const descriptionHtml = description || "";
  const [descriptionState, setDescriptionState] = React.useState(
    editorStateFromHtml(descriptionHtml),
  );

  useEffect(() => {
    setDescriptionState(editorStateFromHtml(descriptionHtml));
  }, [descriptionHtml]);

  return (
    <Box
      sx={{
        paddingTop: "30px",
        paddingBottom: "90px",
        position: "relative",
        width: "100%",
      }}
    >
      <Formik
        initialValues={{
          name,
          description: description || "",
          url,
          publicFacing: publicFacing !== undefined ? publicFacing : false,
        }}
        validateOnBlur={true}
        validateOnChange={true}
        validationSchema={yup.object({
          name: yup.string().trim().required("Name is required"),
          url: yup
            .string()
            .trim()
            .test(
              "is-valid-url",
              "Please enter a valid URL (e.g., https://example.com)",
              (value) => {
                if (!value || value.trim() === "") {
                  return true; // URL is optional
                }
                return yup.string().url().isValidSync(value);
              },
            ),
          publicFacing: yup.boolean(),
        })}
        onSubmit={async (values, submitProps) => {
          const newValues = Object.keys(values).reduce((acc, key) => {
            const _acc = acc;
            if (values[key] !== undefined) _acc[key] = values[key];
            return _acc;
          }, {});

          if (creatingNew) {
            await submitProps.resetForm({ values });
            await dispatch(createShow({ values: newValues, organizationId }));
            navigate("../");
          } else {
            await submitProps.resetForm({ values });
            await dispatch(updateShow({ showId, values: newValues }));
          }
        }}
      >
        {({
          values,
          touched,
          errors,
          handleChange,
          handleBlur,
          isValid,
          dirty,
          setValues,
          setFieldValue,
          handleReset,
          setFieldTouched,
        }) => (
          <Form noValidate autoComplete="off">
            <FormPrompt hasUnsavedChanges={dirty} />

            <NameForm
              formikProps={{
                values,
                handleChange,
                handleBlur,
                touched,
                errors,
              }}
            />

            <NameForm
              formikProps={{
                values,
                handleChange,
                handleBlur: async (e) => {
                  // Normalize URL on blur - add https:// if no protocol
                  const value = e.target.value?.trim();
                  if (
                    value &&
                    !value.startsWith("http://") &&
                    !value.startsWith("https://")
                  ) {
                    await setFieldValue("url", `https://${value}`);
                  }
                  // Always mark as touched and validate
                  await setFieldTouched("url", true);
                  handleBlur(e);
                },
                touched,
                errors,
              }}
              labelOverride={"Website URL"}
              keyOverride={"url"}
              requiredOverride={false}
            />

            <Box sx={{ marginTop: "30px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.publicFacing === true}
                      onChange={(e) => {
                        setFieldValue("publicFacing", e.target.checked);
                      }}
                      name="publicFacing"
                    />
                  }
                  label="Public Facing"
                />
                <Tooltip title="When enabled, this show will have a public landing page where customers can discover and browse all events associated with this show.">
                  <IconButton size="small" sx={{ padding: 0.5 }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {values.publicFacing === true && (
              <DescriptionForm
                formikProps={{
                  values,
                  handleChange,
                }}
                descriptionState={descriptionState}
                handleStateChange={setDescriptionState}
              />
            )}

            <BottomButtons
              show={creatingNew || dirty}
              handleDiscard={async () => {
                if (creatingNew) {
                  setDescriptionState(editorStateFromHtml(""));
                  await handleReset();
                } else {
                  setDescriptionState(editorStateFromHtml(descriptionHtml));
                  await handleReset();
                }
              }}
            />
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default ShowBasicInfo;
